import type { ReactNode } from "react";
import type { AmzProductDetail } from "../../../types/amazon";
import { fmtJst, formatYen, summarizeValue } from "../../../lib/amazonContent";
import { Badge } from "../AmzUi";
import type { DetailTab, Tone } from "../helpers";
import type { ProductEditor } from "../useProductEditor";

interface OverviewTabProps {
  detail: AmzProductDetail;
  editor: ProductEditor;
  onGoTab: (tab: DetailTab) => void;
}

interface CardProps {
  label: string;
  sub: string;
  tone: Tone;
  value: ReactNode;
  onClick?: () => void;
}

function OvCard({ label, sub, tone, value, onClick }: CardProps) {
  const body = (
    <>
      <div className="amz-ov-card-text">
        <span>{label}</span>
        <span>{sub}</span>
      </div>
      <Badge tone={tone}>{value}</Badge>
    </>
  );
  return onClick ? (
    <button type="button" className="amz-ov-card click" onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className="amz-ov-card">{body}</div>
  );
}

export function OverviewTab({ detail, editor, onGoTab }: OverviewTabProps) {
  const src = detail.current ?? editor.draft;
  const n = editor.changes.length;
  const err = detail.issues.filter((i) => i.severity === "ERROR").length;
  const warn = detail.issues.filter((i) => i.severity === "WARNING").length;
  const issueTotal = detail.issues.length;
  const jan = summarizeValue(src, "externally_assigned_product_identifier");
  const janText = (() => {
    const e = (src.externally_assigned_product_identifier?.[0] ?? null) as { value?: unknown } | null;
    return e && typeof e.value === "string" ? e.value : jan;
  })();

  const rows: [string, string, boolean][] = [
    ["商品名", summarizeValue(src, "item_name") || "（未設定）", false],
    ["SKU", detail.sku, true],
    ["ASIN", detail.asin ?? "—（未登録）", true],
    ["商品タイプ", detail.productType, true],
    ["JAN/EAN", janText || "—", true],
    ["ブランド", summarizeValue(src, "brand") || "—", false],
    ["現在価格", formatYen(detail.summary.price), false],
    ["Amazon最終同期", detail.registered ? fmtJst(detail.currentSyncedAt) : "—（未登録）", false],
  ];

  return (
    <div className="amz-ov-grid">
      <dl className="amz-kv" style={{ margin: 0 }}>
        {rows.map(([k, v, mono]) => (
          <div key={k} className="amz-kv-row">
            <dt>{k}</dt>
            <dd className={mono ? "amz-mono" : ""}>{v}</dd>
          </div>
        ))}
      </dl>
      <div className="amz-ov-cards">
        <OvCard
          label="未反映の変更"
          sub={detail.registered ? (n ? "ローカル下書きにAmazonとの差分があります" : "Amazonと一致しています") : "未登録SKUの下書きです（新規登録できます）"}
          tone={n ? "accent" : "success"}
          value={n ? `${n}件` : "なし"}
          onClick={n ? () => onGoTab("page") : undefined}
        />
        <OvCard
          label="Listings Issues"
          sub={`ERROR ${err} · WARNING ${warn}`}
          tone={err ? "error" : issueTotal ? "warning" : "success"}
          value={issueTotal ? `${issueTotal}件` : "なし"}
          onClick={() => onGoTab("issues")}
        />
        <OvCard
          label="出品状態"
          sub={detail.summary.channels.length ? `チャネル: ${detail.summary.channels.join(" / ")}（表示のみ）` : "Amazonに出品がありません"}
          tone={detail.registered ? "success" : "neutral"}
          value={detail.summary.status.length ? detail.summary.status.join(" · ") : "未登録"}
        />
        <OvCard label="A+コンテンツ" sub="この画面では未対応です（既存のCLIで管理）" tone="neutral" value="準備中" />
      </div>
    </div>
  );
}
